import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { refreshBilling, type BillingPlan } from '../../../lib/api'
import { useI18n } from '../../../lib/i18n'

/**
 * The thank-you screen, shown when Stripe returns from a completed checkout.
 *
 * It is deliberately not the source of truth. The plan is granted by the
 * webhook, which fires whether or not the customer's browser ever comes back —
 * they can close the tab on the Stripe redirect and the subscription is still
 * theirs. This screen only reports what already happened.
 *
 * That also means the plan may not be visible the instant this renders: the
 * webhook and the redirect race each other. Rather than claim a plan that has
 * not landed, it polls briefly and says so while it waits.
 */

const PLAN_LABEL: Record<BillingPlan, string> = { free: 'Starter', pro: 'Pro', business: 'Business' }

/** Enough to cover a webhook arriving a moment late, without hanging on failure. */
const POLL_ATTEMPTS = 6
const POLL_INTERVAL_MS = 1500

export function CheckoutSuccess({ token, onDone }: { token: string; onDone: () => void }) {
  const { t } = useI18n()
  const [plan, setPlan] = useState<BillingPlan | null>(null)

  useEffect(() => {
    let alive = true
    let attempts = 0

    const check = async () => {
      try {
        const billing = await refreshBilling(token)
        if (!alive) return
        if (billing.plan !== 'free') { setPlan(billing.plan); return }
      } catch { /* keep polling: a transient failure is not a failed payment */ }
      attempts += 1
      if (alive && attempts < POLL_ATTEMPTS) window.setTimeout(() => void check(), POLL_INTERVAL_MS)
    }
    void check()
    return () => { alive = false }
  }, [token])

  return createPortal(
    <div className="checkout-page checkout-success">
      <div className="checkout-success-card">
        <img className="checkout-brand" src="/veltica.svg" alt="véltica" />

        <div className="checkout-success-mark" aria-hidden="true">✓</div>
        <h1>{plan ? t('checkoutSuccess.titlePlan', { plan: PLAN_LABEL[plan] }) : t('checkoutSuccess.titleThanks')}</h1>
        <p className="checkout-success-lead">
          {t(plan ? 'checkoutSuccess.leadActive' : 'checkoutSuccess.leadPending')}
        </p>

        <ul className="checkout-success-next">
          <li>{t('checkoutSuccess.next1')}</li>
          <li>{t('checkoutSuccess.next2')}</li>
          <li>{t('checkoutSuccess.next3')}</li>
        </ul>

        <button type="button" className="checkout-success-cta" onClick={onDone}>
          {t('checkoutSuccess.cta')}
        </button>
      </div>
    </div>,
    document.body,
  )
}
